<script setup>
import { computed } from 'vue';
import { useMessageContext } from '../provider.js';

import Icon from 'dashboard/components-next/icon/Icon.vue';
import BaseBubble from 'next/message/bubbles/Base.vue';

const KONTA_STATUS = {
  IN_PROGRESS: 'in_progress',
  RINGING: 'ringing',
  COMPLETED: 'completed',
  NO_ANSWER: 'no_answer',
  FAILED: 'failed',
};

const LABEL_MAP = {
  [KONTA_STATUS.IN_PROGRESS]: 'CONVERSATION.VOICE_CALL.CALL_IN_PROGRESS',
  [KONTA_STATUS.COMPLETED]: 'CONVERSATION.VOICE_CALL.CALL_ENDED',
};

const ICON_MAP = {
  [KONTA_STATUS.IN_PROGRESS]: 'i-ph-phone-call',
  [KONTA_STATUS.NO_ANSWER]: 'i-ph-phone-x',
  [KONTA_STATUS.FAILED]: 'i-ph-phone-x',
};

const BG_COLOR_MAP = {
  [KONTA_STATUS.IN_PROGRESS]: 'bg-n-teal-9',
  [KONTA_STATUS.RINGING]: 'bg-n-teal-9 animate-pulse',
  [KONTA_STATUS.COMPLETED]: 'bg-n-slate-11',
  [KONTA_STATUS.NO_ANSWER]: 'bg-n-ruby-9',
  [KONTA_STATUS.FAILED]: 'bg-n-ruby-9',
};

const { contentAttributes } = useMessageContext();

const data = computed(() => contentAttributes.value?.data);
const status = computed(() => data.value?.status?.toString());
const isOutbound = computed(() => data.value?.call_direction === 'outbound');

const isFailed = computed(() =>
  [KONTA_STATUS.NO_ANSWER, KONTA_STATUS.FAILED].includes(status.value)
);

const labelKey = computed(() => {
  if (LABEL_MAP[status.value]) return LABEL_MAP[status.value];
  if (status.value === KONTA_STATUS.RINGING) {
    return isOutbound.value
      ? 'CONVERSATION.VOICE_CALL.OUTGOING_CALL'
      : 'CONVERSATION.VOICE_CALL.INCOMING_CALL';
  }
  return isFailed.value
    ? 'CONVERSATION.VOICE_CALL.MISSED_CALL'
    : 'CONVERSATION.VOICE_CALL.INCOMING_CALL';
});

const iconName = computed(() => {
  if (ICON_MAP[status.value]) return ICON_MAP[status.value];
  return isOutbound.value ? 'i-ph-phone-outgoing' : 'i-ph-phone-incoming';
});

const bgColor = computed(() => BG_COLOR_MAP[status.value] || 'bg-n-teal-9');
</script>

<template>
  <BaseBubble class="p-0 border-none" hide-meta>
    <div class="flex overflow-hidden flex-col w-full max-w-xs">
      <div class="flex gap-3 items-center p-3 w-full">
        <div
          class="flex justify-center items-center rounded-full size-10 shrink-0"
          :class="bgColor"
        >
          <Icon
            class="size-5"
            :icon="iconName"
            :class="{
              'text-n-slate-1': status === KONTA_STATUS.COMPLETED,
              'text-white': status !== KONTA_STATUS.COMPLETED,
            }"
          />
        </div>

        <div class="flex overflow-hidden flex-col flex-grow">
          <span class="text-sm font-medium truncate text-n-slate-12">
            {{ $t(labelKey) }}
          </span>
        </div>
      </div>
    </div>
  </BaseBubble>
</template>
